import React, { useEffect, useState, useRef } from 'react';
import { useBackend } from '../hooks/useBackend';

interface BotsLogsModalProps {
  isOpen: boolean;
  onClose: () => void;
  groupId: string;
  containerName: string;
}

interface LogsResponse {
  success: boolean;
  logs?: string;
  container_name?: string;
  groupId?: string;
  status?: string;
  message?: string;
}

const BotsLogsModal: React.FC<BotsLogsModalProps> = ({ isOpen, onClose, groupId, containerName }) => {
  const [logs, setLogs] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const logsContainerRef = useRef<HTMLPreElement>(null);
  const backend = useBackend();

  useEffect(() => {
    if (isOpen && groupId) {
      fetchLogs();
    }
  }, [isOpen, groupId]); // eslint-disable-line react-hooks/exhaustive-deps

  const scrollToBottom = () => {
    if (logsContainerRef.current) {
      logsContainerRef.current.scrollTop = logsContainerRef.current.scrollHeight;
    }
  };

  const fetchLogs = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const data = await backend.getBotLogs(groupId) as LogsResponse;
      
      if (data.success && data.logs) {
        setLogs(data.logs);
        // Scroll to bottom after logs are set
        setTimeout(scrollToBottom, 100);
      } else {
        throw new Error(data.message || 'Failed to fetch logs');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
      setError(errorMessage);
      setLogs('');
    } finally {
      setLoading(false);
    }
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-backdrop" onClick={handleBackdropClick}>
      <div className="modal-content logs-modal">
        <div className="modal-header">
          <h3>🤖 Bot Logs - {groupId}</h3>
          <button className="modal-close-btn" onClick={onClose}>
            ×
          </button>
        </div>
        
        <div className="modal-body">
          <div className="logs-info">
            <span><strong>Container:</strong> {containerName}</span>
            <button className="refresh-logs-btn" onClick={fetchLogs} disabled={loading}>
              {loading ? '🔄 Loading...' : '🔄 Refresh'}
            </button>
          </div>
          
          <div className="logs-container">
            {loading && <div className="logs-loading">Loading logs...</div>}
            {error && (
              <div className="logs-error">
                <strong>Error:</strong> {error}
              </div>
            )}
            {!loading && !error && (
              <pre className="logs-content" ref={logsContainerRef}>
                {logs || 'No logs available'}
              </pre>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BotsLogsModal;
